<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import * as api from "@/api/GepService";
import { apiCallWithRetry } from "@/api/api-call-with-retry";
import { useConfigStore } from "@/stores/store.config";
import { useRateUsPrompt } from "@/composables/useRateUsPrompt";
import { formatRateUsSubmittedHeading } from "@/utils/rateUsCopy";
import BaseModal from "@/components/BaseModal.vue";
import GuestInfoModal from "@/components/GuestInfoModal.vue";
import VButton from "@/components/VButton.vue";
import VLabel from "@/components/VLabel.vue";
import VRatingStar from "@/components/VRatingStar.vue";
import VTextarea from "@/components/VTextarea.vue";

const user = useConfigStore().useUserStore();
const route = useRoute();
const prompt = useRateUsPrompt();
const customerDetails = ref<DynamicValue>(null);
const rating = ref(5);
const feedback = ref("");
const guestName = ref("");
const email = ref("");
const phoneNumber = ref("");
const showGuestForm = ref(false);
const submitting = ref(false);
const responseMessage = ref<string | null>(null);

const labels = computed(() => user.currentLang.labels || {});
const submittedHeading = computed(() =>
  formatRateUsSubmittedHeading(labels.value.Rateussubmit_desc || "Thank you"),
);
const lang = computed(
  () =>
    user.currentSelectedLang ||
    String(route.query.lang || "") ||
    user.defaultLang ||
    "en",
);
const roomNumber = computed(
  () => customerDetails.value?.custom_attributes?.user_room_number || "",
);

const reset = () => {
  customerDetails.value = null;
  rating.value = 5;
  feedback.value = "";
  guestName.value = "";
  email.value = "";
  phoneNumber.value = "";
  showGuestForm.value = false;
  responseMessage.value = null;
};

const close = () => {
  const submitted = Boolean(responseMessage.value);
  prompt.close();
  reset();
  if (submitted) user.checkTaskRevBooster("RATE_US");
};

const submit = async () => {
  if (submitting.value) return;
  if (!customerDetails.value?.firstname) {
    showGuestForm.value = true;
    return;
  }

  submitting.value = true;
  try {
    const response = await api.submitRating({
      lang: lang.value,
      token: user.token || "",
      payload: {
        ratingPayload: {
          rateStar: rating.value,
          comments: feedback.value,
          merchantId: user.merchantId,
          roomNumber: roomNumber.value,
          guestName: customerDetails.value.firstname,
          tags: "RATE_US",
          email: email.value,
          phoneNumber: phoneNumber.value,
          lang: lang.value,
        },
      },
    });
    responseMessage.value =
      response?.message || labels.value.Rateussubmit_desc || "Thank you";
  } catch (error) {
    console.error(error);
  } finally {
    submitting.value = false;
  }
};

const submitGuestInfo = async (guest: {
  name: string;
  email: string;
  phoneNumber: string;
  roomNumber: string;
  enableNotification: boolean;
}) => {
  await api.updateCustomer({
    token: user.token || "",
    payload: {
      user_room_number: guest.roomNumber,
      firstname: guest.name,
      email: guest.email,
      phoneNumber: guest.phoneNumber,
      enableNotification: guest.enableNotification,
      lastname: "",
    },
  });
  guestName.value = guest.name;
  email.value = guest.email;
  phoneNumber.value = guest.phoneNumber;
  customerDetails.value = {
    ...(customerDetails.value || {}),
    firstname: guest.name,
    custom_attributes: {
      ...(customerDetails.value?.custom_attributes || {}),
      user_room_number: guest.roomNumber,
    },
  };
  showGuestForm.value = false;
  await submit();
};

watch(
  () => prompt.isOpen.value,
  async (open) => {
    if (!open) return;
    reset();
    if (!prompt.isManual.value && prompt.preparedCustomerDetails.value) {
      customerDetails.value = prompt.preparedCustomerDetails.value;
      return;
    }
    try {
      customerDetails.value = await apiCallWithRetry(api.getCustomerDetails, {
        lang: lang.value,
        token: user.token || "",
      });
    } catch (error) {
      console.error(error);
      if (!prompt.isManual.value) prompt.close();
    }
  },
);
</script>

<template>
  <BaseModal
    :open="prompt.isOpen.value && !showGuestForm"
    size="medium"
    :z-index="80"
    :aria-label="labels.Rateus_label || 'Rate us'"
    @close="close"
  >
    <form class="rating-modal" @submit.prevent="submit">
      <button
        type="button"
        class="rating-modal__close"
        :aria-label="labels.Close || 'Close'"
        @click="close"
      >
        ×
      </button>
      <VLabel
        weight="regular-2"
        size="18px"
        :dimension="{ margin: '0 0 8px', padding: '0 8px' }"
      >
        {{
          responseMessage
            ? submittedHeading
            : !prompt.isManual.value && lang.toLowerCase().startsWith("en")
              ? "How has your stay been so far?"
              : labels.Rateusentries_desc ||
                labels.Form_desc ||
                "How has your stay been so far?"
        }}
      </VLabel>
      <VRatingStar
        v-model="rating"
        :value="rating"
        :max="5"
        :min="1"
        :static="Boolean(responseMessage)"
        :dimension="{ margin: '0 0 8px' }"
      />
      <VLabel v-if="responseMessage">{{ responseMessage }}</VLabel>
      <VTextarea
        v-if="!responseMessage"
        v-model="feedback"
        :placeholder="
          !prompt.isManual.value && lang.toLowerCase().startsWith('en')
            ? 'Optional comments'
            : labels.Rateus_textplaceholder || 'Optional comments'
        "
        min-height="76px"
      />
      <VButton
        v-if="!responseMessage"
        class="rating-modal__submit"
        type="submit"
        :caption="labels.Submit_button || 'Submit'"
        :disabled="submitting"
        width="100%"
        :dimension="{ margin: '8px 0' }"
      />
    </form>
  </BaseModal>
  <GuestInfoModal
    :open="showGuestForm"
    :name="guestName"
    :email="email"
    :phone-number="phoneNumber"
    :room-number="roomNumber"
    :generic-qr-code="user.genericQRCode === true"
    @close="showGuestForm = false"
    @submit="submitGuestInfo"
  />
</template>

<style scoped>
.rating-modal {
  position: relative;
  padding-top: 32px;
  text-align: center;
}
.rating-modal__close {
  position: absolute;
  top: 0;
  right: 0;
  display: grid;
  width: 28px;
  height: 28px;
  padding: 0;
  place-items: center;
  border: 0;
  background: transparent;
  font-size: var(--font-size-display);
  cursor: pointer;
}
.rating-modal__submit {
  cursor: pointer;
}
</style>
